import * as React from 'react';
import { object, safeParse, string } from 'valibot';
import { AdsSwappingContext } from 'stores/contexts/adsSwapping/ads-swapping-context';
import { useRefProvider } from 'stores/contexts/ref-provider';
import { useStores } from 'stores/use-stores';
import { AdsBySwapping } from './ads-by-swapping';
import { MesoDisplayAd } from './meso-display-ad';
import { DisplayAd } from 'constants/ads';
const AD_LOAD_PASS = 'AD-LOAD-PASS';
const MESO_RENDER = 'mesoRender';
export const MesoEventDetailSchema = object({
  code: string(),
  uci: string()
});
const R1 = React.memo(() => React.createElement(MesoDisplayAd, Object.assign({}, DisplayAd.SIDERAIL_R1)));
const R2 = React.memo(() => React.createElement(MesoDisplayAd, Object.assign({}, DisplayAd.SIDERAIL_R2)));
export const MesoDynamicSideRail = React.memo(({
  isBEXGSplitViewExperimentEnabled
}) => {
  const {
    advertising
  } = useStores();
  const [mesoDisplayAdR1Ref, mesoDisplayAdR2Ref, adParentDivRef] = useRefProvider('meso-display-ad-R1', 'meso-display-ad-R2', 'ad-parent-div');
  const {
    adLoadState,
    setAdLoadState
  } = React.useContext(AdsSwappingContext);
  const [isMesoSideRailConfigured, setIsMesoSideRailConfigured] = React.useState(true);
  React.useEffect(() => {
    const handleMesoAdEvent = (event, uci) => {
      const result = safeParse(MesoEventDetailSchema, event.detail);
      if (result.success && result.output.uci.includes(uci)) {
        const code = result.output.code;
        setAdLoadState(Object.assign(Object.assign({}, adLoadState), {
          [uci.toLowerCase()]: code === AD_LOAD_PASS
        }));
      } else {
        setAdLoadState(Object.assign(Object.assign({}, adLoadState), {
          [uci.toLowerCase()]: false
        }));
      }
    };
    const addMesoAdEventListener = (mesoAdRef, handleMesoAdEvent) => {
      if (advertising.canTrack && (mesoAdRef === null || mesoAdRef === void 0 ? void 0 : mesoAdRef.current)) {
        mesoAdRef.current.addEventListener('mesoAd', handleMesoAdEvent);
      }
    };
    const setAdVisibility = () => {
      const hideAdParentDiv = () => {
        var _a;
        (_a = adParentDivRef === null || adParentDivRef === void 0 ? void 0 : adParentDivRef.current) === null || _a === void 0 ? void 0 : _a.setAttribute('hidden', '');
      };
      const showAdParentDiv = () => {
        var _a;
        (_a = adParentDivRef === null || adParentDivRef === void 0 ? void 0 : adParentDivRef.current) === null || _a === void 0 ? void 0 : _a.removeAttribute('hidden');
      };
      const shouldShowSideRail = isMesoSideRailConfigured && advertising.canTrack;
      const slotsExpectOrHaveContent = adLoadState.r1 !== false || isBEXGSplitViewExperimentEnabled && adLoadState.r2 !== false;
      if (shouldShowSideRail && slotsExpectOrHaveContent) {
        return showAdParentDiv();
      }
      return hideAdParentDiv();
    };
    const removeMesoAdEventListener = (mesoAdRef, handleMesoAdEvent) => {
      if (advertising.canTrack && (mesoAdRef === null || mesoAdRef === void 0 ? void 0 : mesoAdRef.current)) {
        mesoAdRef.current.removeEventListener('mesoAd', handleMesoAdEvent);
      }
    };
    const handleMesoAdR1Event = event => handleMesoAdEvent(event, 'R1');
    if (mesoDisplayAdR1Ref) {
      addMesoAdEventListener(mesoDisplayAdR1Ref, handleMesoAdR1Event);
    }
    // For Ad R2:
    const handleMesoAdR2Event = event => handleMesoAdEvent(event, 'R2');
    if (mesoDisplayAdR2Ref) {
      addMesoAdEventListener(mesoDisplayAdR2Ref, handleMesoAdR2Event);
    }
    const handleMesoWindowEvent = event => {
      var _a;
      const configuredSlots = Object.keys((_a = event === null || event === void 0 ? void 0 : event.detail) === null || _a === void 0 ? void 0 : _a.content['MESO-DISPLAY-AD']);
      const hasSideRailSlot = configuredSlots.some(slot => ['R1', 'R2'].includes(slot));
      setIsMesoSideRailConfigured(hasSideRailSlot);
    };
    window.addEventListener(MESO_RENDER, handleMesoWindowEvent);
    // The logic according to the view conditions
    setAdVisibility();
    // For both:
    return () => {
      removeMesoAdEventListener(mesoDisplayAdR1Ref, handleMesoAdR1Event);
      removeMesoAdEventListener(mesoDisplayAdR2Ref, handleMesoAdR2Event);
      window.removeEventListener(MESO_RENDER, handleMesoWindowEvent);
    };
  }, [adLoadState, adParentDivRef, advertising.canTrack, isBEXGSplitViewExperimentEnabled, isMesoSideRailConfigured, mesoDisplayAdR1Ref, mesoDisplayAdR2Ref, setAdLoadState]);
  if (isBEXGSplitViewExperimentEnabled) {
    return React.createElement(AdsBySwapping, null);
  }
  return React.createElement(React.Fragment, null, React.createElement(R1, null), React.createElement(R2, null));
});
MesoDynamicSideRail.displayName = 'MesoDynamicSideRail';